import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, ArrowUpRight, Calendar } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StickyCall } from "../components/StickyCall";
import { Reveal } from "../components/Reveal";
import { BLOG_POSTS } from "../data/blog";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function BlogIndex() {
  const [featured, ...rest] = BLOG_POSTS;
  return (
    <div data-testid="blog-index">
      <Seo
        title="Passport & Visa Resources Blog | Urgent Passport & Visa"
        description="Expert guides on expedited passports, visa rules, embassy updates, and travel document tips from Washington DC's trusted passport & visa specialists."
        path="/blog"
      />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <section className="relative bg-[#0b192c] text-white overflow-hidden" data-testid="blog-hero">
          <div className="noise-overlay" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-16 md:pt-20 md:pb-20">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" onClick={scrollTop} className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
              <span>/</span><span className="text-white/90">Resources</span>
            </nav>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">Resources & News</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-[56px] tracking-tight max-w-3xl">
              Passport & visa insights that keep you moving.
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
              Processing-time updates, embassy rule changes, and step-by-step checklists from the specialists who expedite documents every day.
            </p>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <Reveal>
              <Link to={`/blog/${featured.slug}`} onClick={scrollTop} className="group grid lg:grid-cols-2 gap-8 items-center mb-16" data-testid="blog-featured">
                <div className="relative rounded-2xl overflow-hidden h-64 lg:h-80 border border-black/5">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div>
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">{featured.category}</span>
                  <h2 className="mt-3 font-display font-bold text-2xl md:text-4xl text-[#0b192c] tracking-tight leading-tight group-hover:text-[#d90429] transition-colors">{featured.title}</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-5 flex items-center gap-4 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {fmt(featured.date)}</span>
                    <span>{featured.readTime}</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-[#0b192c] group-hover:gap-2 transition-[gap]">Read <ArrowUpRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            </Reveal>

            <div className="grid md:grid-cols-3 gap-8">
              {rest.map((p, i) => (
                <motion.div key={p.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                  <Link to={`/blog/${p.slug}`} onClick={scrollTop} className="group block" data-testid={`blog-card-${i}`}>
                    <div className="relative rounded-2xl overflow-hidden h-52 border border-black/5 mb-5">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">{p.category}</span>
                    <h3 className="mt-2 font-display font-bold text-xl text-[#0b192c] leading-tight group-hover:text-[#d90429] transition-colors">{p.title}</h3>
                    <div className="mt-3 flex items-center gap-3 text-sm text-slate-500">
                      <span>{fmt(p.date)}</span><span>·</span><span>{p.readTime}</span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
